import { Button, Input } from '@nextui-org/react'
import React from 'react'

interface CustomDateRangePickerProps {
    open: boolean
    onClose: () => void
    onExport: () => void
    start: string
    end: string
    setStart: (v: string) => void
    setEnd: (v: string) => void
}

// Suma un mes calendario a una fecha 'YYYY-MM-DD' (sin desfase por zona horaria).
// Si el dia no existe en el mes siguiente (ej: 31/01 -> 31/02), se ajusta al ultimo dia del mes.
const addOneMonth = (date: string): string => {
  const [year, month, day] = date.split('-').map(Number)
  if (!year || !month || !day) return ''
  const target = new Date(Date.UTC(year, month, day))
  if (target.getUTCMonth() !== month % 12) target.setUTCDate(0)
  return target.toISOString().slice(0, 10)
}

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({ open, onClose, onExport, start, end, setStart, setEnd }) => {
  if (!open) return null

  const maxEnd = start ? addOneMonth(start) : ''
  const isRangeTooLong = Boolean(start && end && maxEnd && end > maxEnd)
  const isRangeInverted = Boolean(start && end && end < start)
  const isRangeInvalid = isRangeTooLong || isRangeInverted

  const handleStartChange = (value: string): void => {
    setStart(value)
    // Si el rango queda fuera del maximo de 1 mes, se limpia la fecha final
    const newMaxEnd = value ? addOneMonth(value) : ''
    if (end && (end < value || (newMaxEnd && end > newMaxEnd))) setEnd('')
  }

  return (
    <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50'>
      <div className='bg-white p-6 rounded shadow-lg flex flex-col gap-4 min-w-[320px]'>
        <h3 className='font-bold'>Selecciona el rango de fechas</h3>
        <p className='text-xs text-gray-500'>El rango maximo permitido es de 1 mes.</p>
        <div className='flex gap-2'>
          <Input
            type='date'
            value={start}
            onChange={e => handleStartChange(e.target.value)}
            variant='flat'
            className='px-2 py-1'
          />
          <span className='flex items-center'>-</span>
          <Input
            type='date'
            value={end}
            min={start || undefined}
            max={maxEnd || undefined}
            isDisabled={!start}
            onChange={e => setEnd(e.target.value)}
            variant='flat'
            className='px-2 py-1'
          />
        </div>
        {isRangeInvalid && (
          <p className='text-xs text-red-500'>
            {isRangeInverted
              ? 'La fecha final debe ser posterior a la fecha inicial.'
              : 'El rango no puede superar 1 mes.'}
          </p>
        )}
        <div className='flex gap-2 justify-end'>
          <Button color='danger' onPress={() => { onClose(); setStart(''); setEnd('') }}>
            Cancelar
          </Button>
          <Button
            color='primary'
            isDisabled={!start || !end || isRangeInvalid}
            onPress={onExport}
          >
            Exportar
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CustomDateRangePicker
